import { Component } from '@theme/component';
import { fetchCartState } from '@theme/cart-state';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const ADDED_STATE_DURATION = 2000;

/**
 * Cached recommendation cards markup per seed product id, so re-opening
 * the drawer or morph-driven reconnects don't refetch.
 * @type {Map<string, string>}
 */
const recommendationsCache = new Map();

/**
 * "Buy it again" card markup per product id, harvested from the
 * #cart-drawer-upsell-cart-products block of the recommendations section.
 * The recommendations API never returns the seed product itself, so these
 * are the only way an in-cart product can appear in the carousel.
 *
 * The section only renders cards for buy-again-ELIGIBLE products
 * (custom.buy_again metafield, or complementary to another cart item).
 * Ineligible products are cached as an empty string so we remember they
 * were checked and don't refetch the section for them on every cart
 * update; a later fetch overwrites the sentinel if they become eligible.
 * @type {Map<string, string>}
 */
const buyAgainCardCache = new Map();

/**
 * Upsell carousel for the cart drawer.
 *
 * In automatic mode (no curated product list) it fetches complementary
 * product recommendations seeded from the first cart item and injects
 * the cards; the element carries data-skip-subtree-update /
 * data-skip-node-update so injected cards survive cart morphs, and it
 * re-filters or refetches on cart:update. In curated mode the cards
 * are server-rendered and kept fresh by the morphs themselves.
 *
 * Adds are stamped with attribution line item properties (_upsell,
 * _variant_sku, _product_id, IOM context) via window.Attribution.
 *
 * @typedef {Object} CartUpsellComponentRefs
 * @property {HTMLUListElement} track - The scroll-snap card track.
 * @property {HTMLElement} controls - The prev/next controls wrapper.
 *
 * @extends {Component<CartUpsellComponentRefs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['track', 'controls'];

  /** @type {AbortController | null} */
  #activeFetch = null;
  /** Monotonic token so an older cart refresh can't clobber a newer one. */
  #refreshToken = 0;
  /** @type {string | null} */
  #loadedSeedId = null;
  /** Monotonic token so an older multi-seed load can't clobber a newer one. */
  #loadToken = 0;

  /**
   * Cart quantity per variant id, from the last cart we saw. Cached because
   * the cap has to be re-derived for a single button (a variant change, a
   * freshly injected card) with no cart payload in hand.
   * @type {Record<string, number> | null}
   */
  #variantQuantities = null;

  get #isAutomatic() {
    return this.hasAttribute('data-skip-subtree-update');
  }

  get #maxPerVariant() {
    return Number(this.dataset.maxPerVariant) || 0;
  }

  get #inCartProductIds() {
    return new Set(
      (this.dataset.cartProductIds ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  /** When set, in-cart products stay visible and can be added again. */
  get #showInCart() {
    return this.hasAttribute('data-show-in-cart');
  }

  /**
   * Products whose cart line carries the _upsell property — i.e. already
   * added from this carousel. Their cards are always filtered out (the
   * offer is consumed), even in show-in-cart mode, and reappear if the
   * line is removed from the cart.
   */
  get #upsellAddedProductIds() {
    return new Set(
      (this.dataset.upsellProductIds ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  #handleCartUpdate = (/** @type {CustomEvent | Event} */ event) => {
    // Curated carousels get data-max-per-variant too (the attribute is
    // emitted outside the {% unless is_curated %} block), so they have to
    // track the cart for caps even though they never rebuild their cards.
    // With the cap off they still do nothing at all, as before.
    if (!this.#isAutomatic && this.#maxPerVariant <= 0) return;

    const cart = /** @type {any} */ (event).detail?.resource;

    if (cart?.items) {
      if (this.#isAutomatic) this.#applyCart(cart);
      else this.#applyVariantCaps(cart);
    } else {
      // Some dispatch sites carry no cart payload (add errors, failed cart
      // refetch). The morph can't refresh our data attributes either (blocked
      // by data-skip-node-update), so fetch the cart instead of going stale.
      this.#refreshCartState();
    }
  };

  /** @param {{ items: Array<{ product_id: number, properties?: Record<string, string> | null }> }} cart */
  /**
   * Per-variant cart cap (settings.cart_max_per_variant via
   * data-max-per-variant): disables card Add buttons whose variant already
   * has the maximum quantity in the cart, counting ACROSS lines — an
   * `_upsell` line and a plain line of the same variant are separate cart
   * lines but one variant. Re-enables when quantities drop. UI-only
   * enforcement, same contract as the PDP selector's canAddToCart().
   * @param {{items: Array<{variant_id: number, quantity: number}>}} cart
   */
  #applyVariantCaps(cart) {
    if (this.#maxPerVariant <= 0) return;
    /** @type {Record<string, number>} */
    const perVariant = {};
    for (const item of cart.items) {
      const key = String(item.variant_id);
      perVariant[key] = (perVariant[key] || 0) + item.quantity;
    }
    this.#variantQuantities = perVariant;
    this.#syncVariantCaps();
  }

  /**
   * Re-applies the cap to every card button from the cached counts. Called
   * after each cart read AND after every card injection — recommendations and
   * buy-again cards are written straight into the track, and buy-again cards
   * in particular are by definition for products already in the cart, i.e.
   * the population most likely to be capped.
   */
  #syncVariantCaps() {
    if (this.#maxPerVariant <= 0 || !this.#variantQuantities) return;
    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.querySelectorAll('button[data-variant-id]')
    );
    for (const button of buttons) this.#applyCapToButton(button);
  }

  /**
   * Cap state for ONE button, written unconditionally rather than on the
   * capped/wasCapped transition. The transition form could not recover once
   * anything else touched `disabled` — a variant change re-enabled the button
   * while leaving data-max-capped attached, and the "uncap" branch then never
   * fired again because the attribute said it was still capped.
   *
   * Sold-out always wins: data-unavailable is the button's own record of
   * availability (Liquid renders it, handleVariantChange maintains it), so
   * lifting a cap can never re-enable a variant that has no stock.
   * @param {HTMLButtonElement} button
   */
  #applyCapToButton(button) {
    const max = this.#maxPerVariant;
    if (max <= 0 || !this.#variantQuantities) return;

    const capped = (this.#variantQuantities[String(button.dataset.variantId)] || 0) >= max;
    const unavailable = button.hasAttribute('data-unavailable');

    button.toggleAttribute('data-max-capped', capped);
    button.disabled = unavailable || capped;

    const message = this.dataset.maxPerVariantMessage;
    if (capped && message) button.title = message;
    else if (!capped) button.removeAttribute('title');
  }

  /** True when this variant is already at the cap. @param {string} variantId */
  #isVariantCapped(variantId) {
    const max = this.#maxPerVariant;
    if (max <= 0 || !this.#variantQuantities) return false;
    return (this.#variantQuantities[String(variantId)] || 0) >= max;
  }

  #applyCart(cart) {
    this.#applyVariantCaps(cart);
    const newestId = cart.items[0] ? String(cart.items[0].product_id) : null;
    const inCartIds = cart.items.map((item) => String(item.product_id));
    this.dataset.cartProductIds = inCartIds.join(',');
    this.dataset.upsellProductIds = cart.items
      .filter((item) => item.properties?.['_upsell'])
      .map((item) => String(item.product_id))
      .join(',');

    if (!newestId) {
      this.hidden = true;
      return;
    }

    // /cart.js lists the newest line first, so after an upsell add the
    // "first item" IS the upsell. Keep the loaded card set stable while
    // its seed product is still in the cart — filtering alone removes the
    // just-added card — and only reseed when the seed left the cart or
    // filtering leaves nothing to show.
    const seedStillInCart = this.#loadedSeedId !== null && inCartIds.includes(this.#loadedSeedId);

    if (!seedStillInCart) {
      this.dataset.firstProductId = newestId;
      this.#loadRecommendations();
    } else if (this.#showInCart) {
      // Cart composition changed: append buy-again cards for newly added
      // products, drop them for removed ones.
      this.#syncBuyAgainCards();
    } else if (this.#filterCards() === 0) {
      // Every loaded card is now in the cart — try the fallback seeds.
      this.dataset.firstProductId = newestId;
      this.#loadRecommendations();
    }
  }

  async #refreshCartState() {
    // Shared, deduplicated /cart.js fetch (see @theme/cart-state): after an
    // add this fires alongside product-form's quantity refresh — one request
    // serves both. The token + isConnected guard replaces the old per-fetch
    // AbortController: only the newest refresh applies, and never after
    // disconnect.
    const token = ++this.#refreshToken;

    try {
      const cart = await fetchCartState();
      if (token !== this.#refreshToken || !this.isConnected) return;
      if (!cart?.items) return;
      if (this.#isAutomatic) this.#applyCart(cart);
      else this.#applyVariantCaps(cart);
    } catch (error) {
      console.error(error);
    }
  }

  /** @type {Element | null} */
  #drawer = null;

  /**
   * Drawer open: start the deferred automatic fetch if needed, then re-read
   * arrow state. `#syncArrowState` is a no-op while the closed dialog is
   * `display: none` (scroll metrics are 0); cart events can load/filter
   * cards in that state, so this is the first chance the arrows can reflect
   * real overflow.
   */
  #handleDialogOpen = () => {
    if (this.#isAutomatic && this.#loadedSeedId === null) this.#loadRecommendations();
    // Seed the caps here, NOT in connectedCallback: #applyCart triggers
    // #loadRecommendations when no seed is loaded, so a connect-time cart read
    // would drag the deferred recommendations fetch back onto page load. Open
    // is also the first moment a cap can matter — the buttons were inside a
    // closed dialog until now. One deduped read, and only while capping.
    if (this.#maxPerVariant > 0 && !this.#variantQuantities) this.#refreshVariantCaps();
    requestAnimationFrame(this.#syncArrowState);
  };

  /**
   * Caps-only cart read. Deliberately NOT #refreshCartState: that routes
   * through #applyCart, which reseeds and can fetch recommendations. Caps are
   * UI-only, so a failed read just leaves them unset.
   */
  async #refreshVariantCaps() {
    if (this.#maxPerVariant <= 0) return;
    try {
      const cart = await fetchCartState();
      if (!this.isConnected || !cart?.items) return;
      this.#applyVariantCaps(cart);
    } catch {
      /* caps are advisory; the server still enforces nothing here */
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.refs.track.addEventListener('scroll', this.#syncArrowState, { passive: true });

    // Listen in both modes — curated cards are already in the DOM and still
    // need an open-time re-sync. Automatic load deferral stays gated below.
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#syncArrowState();

    if (this.#isAutomatic) {
      // Defer the initial recommendations fetch until the drawer actually
      // opens (same pattern as cart-drawer-recently-viewed.js): the carousel
      // lives inside a closed <dialog>, so fetching on connect costs one
      // Section Rendering API request per page view for content the shopper
      // may never see. Cart events still trigger loads while the drawer is
      // closed (it auto-opens on add), and reconnects hit recommendationsCache.
      if (!this.#drawer || this.#drawer.querySelector('dialog[open]')) {
        this.#loadRecommendations();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.refs.track.removeEventListener('scroll', this.#syncArrowState);
    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#drawer = null;
    this.#activeFetch?.abort();
    // Invalidate any pending shared cart refresh (see #refreshCartState).
    this.#refreshToken++;
  }

  async #loadRecommendations() {
    const firstProductId = this.dataset.firstProductId;
    if (!firstProductId || !this.dataset.recommendationsUrl) {
      this.hidden = true;
      return;
    }

    this.#loadedSeedId = firstProductId;
    const token = ++this.#loadToken;

    // Complementary recommendations exist only for products configured in
    // Search & Discovery. When the first cart item yields none, fall back to
    // the other in-cart products instead of hiding for the whole session.
    const seedIds = [firstProductId, ...this.#inCartProductIds].filter(
      (id, index, ids) => ids.indexOf(id) === index
    );

    try {
      let recommendationsHTML = '';

      for (const seedId of seedIds) {
        let cardsHTML = recommendationsCache.get(seedId);

        if (cardsHTML === undefined) {
          await this.#fetchSectionDoc(seedId);
          cardsHTML = recommendationsCache.get(seedId) ?? '';
        }

        // A newer load may have started while we were fetching.
        if (token !== this.#loadToken) return;
        if (!cardsHTML) continue;

        if (this.#showInCart) {
          // Buy-again cards get appended below; the first seed with
          // recommendations wins.
          recommendationsHTML = cardsHTML;
          break;
        }

        this.refs.track.innerHTML = cardsHTML;
        if (this.#filterCards() > 0) return;
      }

      if (!this.#showInCart) {
        // Every seed came up empty (or fully in-cart already).
        this.refs.track.innerHTML = '';
        this.#filterCards();
        return;
      }

      // Show-in-cart mode: recommendations (possibly none) plus a buy-again
      // card for every in-cart product not already covered by one.
      const missingBuyAgain = [...this.#inCartProductIds].some((id) => !buyAgainCardCache.has(id));
      if (missingBuyAgain) {
        await this.#fetchSectionDoc(firstProductId);
        if (token !== this.#loadToken) return;
      }

      this.refs.track.innerHTML = recommendationsHTML;
      this.#appendBuyAgainCards();
      this.#filterCards();
    } catch (error) {
      console.error(error);
      this.hidden = true;
    }
  }

  /**
   * Fetches the recommendations section for a seed and harvests both card
   * sources: the recommendation cards (cached per seed) and the buy-again
   * cards for the cart's own products (cached per product id).
   * @param {string} seedId
   */
  async #fetchSectionDoc(seedId) {
    const url = new URL(this.dataset.recommendationsUrl ?? '', location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId ?? 'cart-drawer-upsell');
    url.searchParams.set('product_id', seedId);
    url.searchParams.set('limit', this.dataset.limit ?? '5');
    url.searchParams.set('intent', 'complementary');

    const response = await fetch(url.toString());
    // Without this check a 5xx / 429 / bot-challenge HTML body parses fine,
    // yields no #cart-drawer-upsell-products, and the '' below gets cached —
    // permanently marking that seed "no recommendations" for the page lifetime.
    // Throwing instead leaves the cache untouched so a later open retries.
    if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    recommendationsCache.set(seedId, doc.getElementById('cart-drawer-upsell-products')?.innerHTML.trim() ?? '');

    const cartProductsBlock = doc.getElementById('cart-drawer-upsell-cart-products');

    for (const card of cartProductsBlock?.querySelectorAll('.cart-upsell__card') ?? []) {
      if (!(card instanceof HTMLElement) || !card.dataset.productId) continue;
      card.setAttribute('data-buy-again', '');
      buyAgainCardCache.set(card.dataset.productId, card.outerHTML);
    }

    // In-cart products the section checked but rendered no card for are
    // not buy-again eligible — cache the empty sentinel (never overwriting
    // a real card, which persists for the session by design).
    for (const id of (cartProductsBlock?.dataset.checkedProductIds ?? '').split(',')) {
      const productId = id.trim();
      if (productId && !buyAgainCardCache.has(productId)) buyAgainCardCache.set(productId, '');
    }
  }

  /**
   * Prepends a buy-again card (buy-again cards lead the carousel) for every
   * product seen in the cart this session that has no card in the track yet.
   * Cards persist after their product is removed from the cart, so removed
   * items return to the carousel as re-add offers; #filterCards hides the
   * ones whose current cart line came from the carousel (_upsell property).
   * Recommendation cards are never touched.
   */
  #appendBuyAgainCards() {
    const { track } = this.refs;
    const inCartIds = this.#inCartProductIds;
    const upsellAddedIds = this.#upsellAddedProductIds;

    const presentIds = new Set(
      [...track.querySelectorAll('.cart-upsell__card')].map((card) =>
        card instanceof HTMLElement ? card.dataset.productId : undefined
      )
    );

    // Current cart products lead (newest first), then products previously
    // seen in the cart this session.
    const offerIds = new Set([...inCartIds, ...buyAgainCardCache.keys()]);

    const missingCardsHTML = [];
    for (const id of offerIds) {
      if (presentIds.has(id) || upsellAddedIds.has(id)) continue;
      const cardHTML = buyAgainCardCache.get(id);
      if (cardHTML) missingCardsHTML.push(cardHTML);
    }

    if (missingCardsHTML.length > 0) {
      // Insert before any existing buy-again cards so newly added products
      // lead, matching the cart's newest-first order.
      track.insertAdjacentHTML('afterbegin', missingCardsHTML.join(''));
    }
  }

  /**
   * Reconciles buy-again cards after a cart change, fetching the section
   * once if a newly added product has no cached card yet.
   */
  async #syncBuyAgainCards() {
    const token = this.#loadToken;
    const { track } = this.refs;

    const upsellAddedIds = this.#upsellAddedProductIds;
    const needsFetch = [...this.#inCartProductIds].some(
      (id) =>
        !upsellAddedIds.has(id) &&
        !buyAgainCardCache.has(id) &&
        !track.querySelector(`.cart-upsell__card[data-product-id="${id}"]`)
    );

    if (needsFetch) {
      const seedId = this.#loadedSeedId ?? [...this.#inCartProductIds][0];
      if (seedId) {
        try {
          await this.#fetchSectionDoc(seedId);
        } catch (error) {
          console.error(error);
        }
      }
      // A reseed started while we were fetching — it renders the track itself.
      if (token !== this.#loadToken) return;
    }

    this.#appendBuyAgainCards();
    this.#filterCards();
  }

  /**
   * Hides cards for products already in the cart (unless data-show-in-cart
   * keeps them addable). Products added FROM the carousel (cart line has
   * the _upsell property) are hidden in either mode — that offer is
   * consumed until the line leaves the cart. Syncs the component/controls
   * visibility with the remaining card count.
   * @returns {number} The number of visible cards.
   */
  #filterCards() {
    const inCartIds = this.#inCartProductIds;
    const upsellAddedIds = this.#upsellAddedProductIds;
    const showInCart = this.#showInCart;
    let visibleCount = 0;

    for (const card of this.refs.track.querySelectorAll('.cart-upsell__card')) {
      if (!(card instanceof HTMLElement)) continue;
      const productId = card.dataset.productId;
      const hide =
        productId !== undefined && inCartIds.has(productId) && (!showInCart || upsellAddedIds.has(productId));
      card.hidden = hide;
      if (!hide) visibleCount += 1;
    }

    this.hidden = visibleCount === 0;
    this.refs.controls.hidden = visibleCount <= 1;
    this.#syncArrowState();
    // Runs after every card injection, so newly written buttons inherit the
    // cap instead of arriving enabled.
    this.#syncVariantCaps();

    return visibleCount;
  }

  #syncArrowState = () => {
    const arrows = this.refs.controls?.querySelectorAll('.cart-upsell__arrow');
    const prevButton = arrows?.[0];
    const nextButton = arrows?.[1];
    if (!(prevButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) return;

    const { track } = this.refs;
    // Closed <dialog> is display:none — scrollLeft/clientWidth/scrollWidth
    // are all 0, which would mark BOTH arrows aria-disabled and make
    // handlePrev/handleNext no-op. Leave the last known state; DialogOpenEvent
    // re-syncs once the track has a real layout.
    if (track.clientWidth === 0) return;

    prevButton.setAttribute('aria-disabled', String(track.scrollLeft <= 2));
    nextButton.setAttribute(
      'aria-disabled',
      String(track.scrollLeft + track.clientWidth >= track.scrollWidth - 2)
    );
  };

  handlePrev = () => {
    const prevButton = this.refs.controls?.querySelectorAll('.cart-upsell__arrow')[0];
    if (prevButton?.getAttribute('aria-disabled') === 'true') return;
    this.#scrollByCard(-1);
  };

  handleNext = () => {
    const nextButton = this.refs.controls?.querySelectorAll('.cart-upsell__arrow')[1];
    if (nextButton?.getAttribute('aria-disabled') === 'true') return;
    this.#scrollByCard(1);
  };

  /**
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByCard(direction) {
    const { track } = this.refs;
    const card = track.querySelector('.cart-upsell__card:not([hidden])');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = card instanceof HTMLElement ? card.offsetWidth + gap : track.clientWidth;

    track.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  /**
   * Syncs a card with the variant chosen in its select. The select
   * carries no name attribute — the hidden input[name='id'] stays the
   * single field IOM resolves and the form posts — so everything the
   * variant drives is updated here: the id input, the add button
   * (variant id, disabled state, label), the price/compare-at nodes,
   * and the attribution SKU.
   * @param {Event} event
   */
  handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const card = select.closest('.cart-upsell__card');
    const option = select.selectedOptions[0];
    if (!(card instanceof HTMLElement) || !option) return;

    const form = card.querySelector('form');
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = option.value;

    const button = form?.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      const available = option.dataset.available !== 'false';
      const labelEl = button.querySelector('.cart-upsell__add-label') ?? button;
      const icon = button.querySelector('.cart-upsell__add-icon');

      button.dataset.variantId = option.value;
      // data-variant-id IS the cap's key, so switching variant has to
      // re-derive it: the new variant may be capped when the old one wasn't,
      // and a bare `disabled = !available` would otherwise hand a capped
      // variant a live Add button.
      button.toggleAttribute('data-unavailable', !available);
      button.disabled = !available;
      // Tightens to disabled when the NEW variant is capped; a no-op when the
      // cap is off, which is why availability is written first.
      this.#applyCapToButton(button);
      if (icon instanceof HTMLElement) icon.hidden = !available;
      const label = available ? button.dataset.addLabel : button.dataset.soldOut;
      if (label) labelEl.textContent = label;
    }

    // One money value per node (IOM contract): swap text, never combine.
    const priceValue = card.querySelector('.cart-upsell__price-value');
    if (priceValue && option.dataset.price) priceValue.textContent = option.dataset.price;

    let compare = card.querySelector('.cart-upsell__compare-price');
    if (option.dataset.compareAtPrice) {
      if (!compare) {
        compare = document.createElement('s');
        compare.className = 'cart-upsell__compare-price';
        card.querySelector('.cart-upsell__price')?.prepend(compare);
      }
      compare.textContent = option.dataset.compareAtPrice;
    } else {
      compare?.remove();
    }

    if (form instanceof HTMLElement && form.dataset.attributionLineProperties) {
      try {
        const properties = JSON.parse(form.dataset.attributionLineProperties);
        if (option.dataset.sku) properties._variant_sku = option.dataset.sku;
        else delete properties._variant_sku;
        form.dataset.attributionLineProperties = JSON.stringify(properties);
      } catch {
        // Malformed JSON — leave the server-rendered value untouched.
      }
    }
  };

  /**
   * Intercepts the card's product form submit and adds the variant to
   * the cart with attribution properties. Without JS the form posts
   * natively to /cart/add (the properties[_upsell] hidden input still
   * applies).
   * @param {SubmitEvent} event
   */
  handleAddToCart = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const formData = new FormData(form);
    const variantId = String(formData.get('id') ?? button.dataset.variantId ?? '');
    if (!variantId) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    // Swap only the label span so the plus icon survives; fall back to the
    // button itself for cached cards harvested before the span existed.
    const labelEl = button.querySelector('.cart-upsell__add-label') ?? button;
    const originalLabel = labelEl.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (button.dataset.addingLabel) labelEl.textContent = button.dataset.addingLabel;

    formData.set('quantity', '1');

    const attribution = /** @type {any} */ (window).Attribution;
    if (attribution && typeof attribution.buildLineItemProperties === 'function') {
      const properties = attribution.buildLineItemProperties(form, variantId) || {};
      if (!properties._upsell) properties._upsell = 'true';
      for (const [key, value] of Object.entries(properties)) {
        formData.set(`properties[${key}]`, String(value));
      }
    } else {
      formData.set('properties[_upsell]', 'true');
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);
    formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
        signal: abortController.signal,
      });
      const data = await response.json();

      if (data.status) {
        console.error(data.description || data.message);
        labelEl.textContent = originalLabel;
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal: abortController.signal });
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'cart-upsell', {
          source: 'cart-upsell',
          itemCount: cart.item_count,
          variantId,
          sections: data.sections,
        })
      );

      if (attribution && typeof attribution.syncCartAttributes === 'function') {
        attribution.syncCartAttributes().catch(() => {});
      }

      if (button.isConnected) {
        if (button.dataset.addedLabel) labelEl.textContent = button.dataset.addedLabel;
        setTimeout(() => {
          // Skip the restore if a variant change flipped the card to sold
          // out while the "Added" state was showing.
          if (button.isConnected && labelEl.textContent !== button.dataset.soldOut) {
            labelEl.textContent = originalLabel;
            // An add that REACHED the cap must not be handed back a live
            // button 2s later, racing the cart refresh that caps it.
            button.disabled = button.hasAttribute('data-unavailable') || this.#isVariantCapped(button.dataset.variantId ?? '');
          }
        }, ADDED_STATE_DURATION);
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error(error);
      }
      labelEl.textContent = originalLabel;
    } finally {
      this.#activeFetch = null;
      button.removeAttribute('aria-busy');
      if (!labelEl.textContent || labelEl.textContent === button.dataset.addingLabel) {
        labelEl.textContent = originalLabel;
      }
      if (labelEl.textContent === originalLabel) {
        button.disabled =
          button.hasAttribute('data-unavailable') || this.#isVariantCapped(button.dataset.variantId ?? '');
      }
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
